import { useEffect, useState, useContext, useRef, useCallback } from 'react';

import { FormControl, Select, MenuItem, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { updateUserViaAdmin } from '@/repository/UserDAO';
import { UserContext } from '@/context_providers/user_context/UserProvider';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { useTranslation } from 'react-i18next';
import {
  ErrorStringKeys,
  StringKeys,
  SuccessStringKeys,
} from '@/resources/strings/StringKeys';
import LoadingData from '../loading/LoadingData';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import ConfirmModal from '../modals/utils/ConfirmModal';
import COLORS from '@/resources/style/Themes';
import { OrgRole, OrgUser } from '@/models/User/OrgUser';

/**
 * Helper function to get role-specific warning messages
 */
const DEFAULT_SELECTOR_FONT_SIZE = 15;

const getRoleWarningMessage = (
  role: OrgRole | null,
  t: (key: string) => string,
): string => {
  switch (role) {
    case 'Admin':
      return t(StringKeys.ADMIN_WARNING);
    case 'Dispatch':
      return t(StringKeys.DISPATCH_WARNING);
    case 'Mobile User':
      return t(StringKeys.MOBILE_USER_WARNING);
    default:
      return t(StringKeys.ARE_YOU_SURE_YOU_WANT_TO_CHANGE_THE_ROLE);
  }
};

interface RoleSelectorProps {
  userToHighlight?: (auth_id: string) => void;
  orgAzureId: string;
  orgCode: string;
  roleList: string[];
  user: OrgUser;
  sx?: SxProps<Theme>;
  fontSize?: number;
}

function RoleSelector({
  userToHighlight = () => {},
  orgAzureId,
  orgCode,
  roleList,
  user,
  sx,
  fontSize = DEFAULT_SELECTOR_FONT_SIZE,
}: RoleSelectorProps) {
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<OrgRole>(user?.role || '');
  const [showSelfDemotionModal, setShowSelfDemotionModal] = useState(false);
  const [showRoleWarningModal, setShowRoleWarningModal] = useState(false);
  const [pendingRole, setPendingRole] = useState<OrgRole | null>(null);

  const latestSelectionRef = useRef(selectedRole);
  const { orgUser } = useUserUpdaterProvider();
  const { bearerToken: _bearerToken } = useContext(UserContext);
  const { showSuccessToast, showErrorToast } = useToast();
  const { t } = useTranslation();
  const { palette } = useTheme();

  // Use orgUser if available (User views), otherwise fall back to contextUser (Safeguard views)
  const currentUser = orgUser;

  // Sync selected role when user changes or role updates
  useEffect(() => {
    if (user) {
      setSelectedRole(user.role);
      latestSelectionRef.current = user.role;
    }
  }, [user]);

  const performRoleUpdate = useCallback(
    async (newRole: OrgRole) => {
      const prevRole = selectedRole;
      latestSelectionRef.current = newRole;
      setLoading(true);
      const userName = `${user.first_name} ${user.last_name}`;

      try {
        const userViaAdmin = {
          org_code: orgCode,
          org_user_azure_id: user.org_user_azure_id || user.id,
          role: newRole,
          organization_azure_id: orgAzureId,
        };

        await updateUserViaAdmin(userViaAdmin);

        if (latestSelectionRef.current === newRole) {
          setSelectedRole(newRole);
          user.role = newRole;
          userToHighlight(user.auth_id);
          showSuccessToast(
            t(SuccessStringKeys.SUCCESSFULLY_UPDATED_USERS_ROLE_TO_ROLE, {
              user: userName,
              role: newRole,
            }),
          );
        }
      } catch {
        if (latestSelectionRef.current === newRole) {
          setSelectedRole(prevRole);
        }
        showErrorToast(
          t(ErrorStringKeys.FAILED_TO_UPDATE_USERS_ROLE, { user: userName }),
        );
      } finally {
        setLoading(false);
        setPendingRole(null);
      }
    },
    [
      selectedRole,
      user,
      orgCode,
      orgAzureId,
      userToHighlight,
      showSuccessToast,
      showErrorToast,
      t,
    ],
  );

  /**
   * Handles confirmation of role warning modal.
   * Checks if self-demotion and shows appropriate modal.
   */
  const handleRoleWarningConfirm = useCallback(() => {
    setShowRoleWarningModal(false);

    if (!pendingRole) return;

    // Check if this is a self-demotion case
    const isSelfDemotion =
      currentUser?.auth_id === user.auth_id &&
      currentUser?.role === 'Admin' &&
      pendingRole !== 'Admin';

    if (isSelfDemotion) {
      setShowSelfDemotionModal(true);
    } else {
      performRoleUpdate(pendingRole);
    }
  }, [pendingRole, currentUser, user, performRoleUpdate]);

  /**
   * Handles confirmation of self-demotion modal
   */
  const handleSelfDemotionConfirm = () => {
    setShowSelfDemotionModal(false);
    if (pendingRole) {
      performRoleUpdate(pendingRole);
    }
  };

  /**
   * Handles role selection change.
   * Shows role warning modal as first step.
   */
  const handleRoleChange = (newRole: OrgRole) => {
    setPendingRole(newRole);
    setShowRoleWarningModal(true);
  };

  /**
   * Handles cancellation of role warning modal
   */
  const handleRoleWarningCancel = () => {
    setShowRoleWarningModal(false);
    setPendingRole(null);
    setSelectedRole(user.role);
  };

  /**
   * Handles cancellation of self-demotion modal
   */
  const handleSelfDemotionCancel = () => {
    setShowSelfDemotionModal(false);
    setPendingRole(null);
    setSelectedRole(user.role);
  };

  return (
    <>
      {user && (
        <FormControl
          variant="outlined"
          sx={[{ width: '80%', minWidth: 120 }, sx]}
        >
          <Select
            value={selectedRole}
            onChange={(e) => {
              const newRole = e.target.value;
              setSelectedRole(newRole);
              handleRoleChange(newRole);
            }}
            disabled={loading}
            sx={{
              backgroundColor: palette.grey[300],
              border: 'none',
              borderRadius: 2,
              height: 40,
              fontSize,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: `solid 2px ${COLORS.BLUE_CRAYOLA}`,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            {roleList.map((item) => (
              <MenuItem key={item} value={item} sx={{ fontSize }}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <LoadingData show={loading} />

      {/* Role Warning Modal - First confirmation */}
      <ConfirmModal
        confirm={handleRoleWarningConfirm}
        show={showRoleWarningModal}
        confirmMessage={getRoleWarningMessage(pendingRole, t)}
        confirmTitle={t(StringKeys.ARE_YOU_SURE_YOU_WANT_TO_DO_THIS)}
        onHide={handleRoleWarningCancel}
      />

      {/* Self-Demotion Modal - Second confirmation for admins demoting themselves */}
      <ConfirmModal
        confirm={handleSelfDemotionConfirm}
        show={showSelfDemotionModal}
        confirmMessage={t(StringKeys.THIS_ACTION_WILL_REMOVE_YOUR_ADMIN_ACCESS)}
        confirmTitle={t(StringKeys.WARNING).toUpperCase()}
        onHide={handleSelfDemotionCancel}
      />
    </>
  );
}

export default RoleSelector;
